import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";

import { parseDocument } from "yaml";

import { CliError, EXIT_INPUT } from "../common.js";
import { isLlmTaskId } from "./registry.js";
import type { LlmProtocol, LlmTaskId } from "./types.js";

export interface LlmProviderConfig {
  protocol: LlmProtocol;
  baseUrl: string | null;
  apiKeyEnv: string;
  timeoutMs: number;
  maxAttempts: number;
}

export interface LlmModelConfig {
  provider: string;
  model: string;
  thinkingBudgetTokens?: number;
  reasoning?: string; // OpenAI-compatible reasoning_effort (e.g. low|medium|high|max)
  temperature?: number; // sampling temperature; 0 = near-deterministic (stabilizes serial merges + resume cache)
}

export interface ResolvedLlmConfig {
  version: 1;
  providers: Readonly<Record<string, LlmProviderConfig>>;
  models: Readonly<Record<string, LlmModelConfig>>;
  tasks: Readonly<Partial<Record<LlmTaskId, string>>>;
  source: "builtin" | "file";
  sourcePath: string | null;
}

type UnknownRecord = Record<string, unknown>;

const PROVIDER_KEYS = new Set(["protocol", "baseUrl", "apiKey", "timeoutMs", "maxAttempts"]);
const MODEL_KEYS = new Set(["provider", "model", "thinking", "reasoning", "temperature"]);
const API_KEY_KEYS = new Set(["env"]);
const THINKING_KEYS = new Set(["budgetTokens"]);

function configError(message: string, received: string[] = []): CliError {
  return new CliError(message, {
    exitCode: EXIT_INPUT,
    errorCode: "LLM_CONFIG_INVALID",
    hint: "Fix the selected LLM configuration or environment and rerun `scriptctl doctor`.",
    data: { expected: ["valid scriptctl LLM YAML configuration"], actual: received },
  });
}

function httpUrlAt(value: string, label: string): string {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(value);
  } catch {
    throw configError(`${label} must be an absolute URL.`);
  }
  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    throw configError(`${label} must use http or https.`);
  }
  return parsedUrl.toString().replace(/\/$/, "");
}

export function normalizeProviderBaseUrl(value: string, label = "provider base URL"): string {
  return httpUrlAt(value, label);
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function recordAt(value: unknown, label: string): UnknownRecord {
  if (!isRecord(value)) throw configError(`${label} must be a mapping.`, [`${label}: ${typeof value}`]);
  return value;
}

function stringAt(value: unknown, label: string): string {
  if (typeof value !== "string" || !value.trim()) throw configError(`${label} must be a non-empty string.`);
  return value.trim();
}

function integerAt(value: unknown, label: string, min: number, max?: number): number {
  if (!Number.isInteger(value) || typeof value !== "number" || value < min || (max !== undefined && value > max)) {
    throw configError(`${label} must be an integer in ${max === undefined ? `[${min}, +∞)` : `[${min}, ${max}]`}.`);
  }
  return value;
}

function rejectUnknownKeys(record: UnknownRecord, allowed: ReadonlySet<string>, label: string): void {
  const unknown = Object.keys(record).filter((key) => !allowed.has(key));
  if (unknown.length > 0) throw configError(`${label} contains unknown fields.`, unknown);
}

function parseProvider(name: string, raw: unknown): LlmProviderConfig {
  const provider = recordAt(raw, `providers.${name}`);
  rejectUnknownKeys(provider, PROVIDER_KEYS, `providers.${name}`);
  const protocolRaw = stringAt(provider["protocol"], `providers.${name}.protocol`);
  if (protocolRaw !== "anthropic" && protocolRaw !== "openai-compatible" && protocolRaw !== "google-gemini") {
    throw configError(`providers.${name}.protocol is unsupported.`, [protocolRaw]);
  }
  // Only openai-compatible needs an explicit endpoint; anthropic and
  // google-gemini use their SDK default when baseUrl is omitted.
  const baseUrl = provider["baseUrl"] === undefined && protocolRaw !== "openai-compatible"
    ? null
    : httpUrlAt(stringAt(provider["baseUrl"], `providers.${name}.baseUrl`), `providers.${name}.baseUrl`);
  const apiKey = recordAt(provider["apiKey"], `providers.${name}.apiKey`);
  rejectUnknownKeys(apiKey, API_KEY_KEYS, `providers.${name}.apiKey`);
  const apiKeyEnv = stringAt(apiKey["env"], `providers.${name}.apiKey.env`);
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(apiKeyEnv)) {
    throw configError(`providers.${name}.apiKey.env must be an environment variable name.`);
  }
  return {
    protocol: protocolRaw,
    baseUrl,
    apiKeyEnv,
    timeoutMs: integerAt(provider["timeoutMs"], `providers.${name}.timeoutMs`, 1),
    maxAttempts: integerAt(provider["maxAttempts"], `providers.${name}.maxAttempts`, 1, 6),
  };
}

function parseModel(name: string, raw: unknown, providers: Readonly<Record<string, LlmProviderConfig>>): LlmModelConfig {
  const model = recordAt(raw, `models.${name}`);
  rejectUnknownKeys(model, MODEL_KEYS, `models.${name}`);
  const providerName = stringAt(model["provider"], `models.${name}.provider`);
  const provider = providers[providerName];
  if (!provider) throw configError(`models.${name} references an unknown provider.`, [providerName]);
  const result: LlmModelConfig = {
    provider: providerName,
    model: stringAt(model["model"], `models.${name}.model`),
  };
  if (model["reasoning"] !== undefined) result.reasoning = stringAt(model["reasoning"], `models.${name}.reasoning`);
  if (model["temperature"] !== undefined) {
    const t = model["temperature"];
    if (typeof t !== "number" || !Number.isFinite(t) || t < 0) throw configError(`models.${name}.temperature must be a number ≥ 0.`);
    result.temperature = t;
  }
  if (model["thinking"] !== undefined) {
    if (provider.protocol !== "anthropic") throw configError(`models.${name}.thinking is only valid for anthropic providers.`);
    const thinking = recordAt(model["thinking"], `models.${name}.thinking`);
    rejectUnknownKeys(thinking, THINKING_KEYS, `models.${name}.thinking`);
    result.thinkingBudgetTokens = integerAt(thinking["budgetTokens"], `models.${name}.thinking.budgetTokens`, 1024);
  }
  return result;
}

export function parseLlmConfig(value: unknown, sourcePath: string): ResolvedLlmConfig {
  const root = recordAt(value, "config");
  rejectUnknownKeys(root, new Set(["version", "providers", "models", "tasks"]), "config");
  if (root["version"] !== 1) throw configError("config.version must be 1.");
  const rawProviders = recordAt(root["providers"], "providers");
  const providers: Record<string, LlmProviderConfig> = {};
  for (const [name, provider] of Object.entries(rawProviders)) providers[name] = parseProvider(name, provider);
  const rawModels = recordAt(root["models"], "models");
  const models: Record<string, LlmModelConfig> = {};
  for (const [name, model] of Object.entries(rawModels)) models[name] = parseModel(name, model, providers);
  if (!models["default"]) throw configError("models.default is required.");
  const tasks: Partial<Record<LlmTaskId, string>> = {};
  if (root["tasks"] !== undefined) {
    const rawTasks = recordAt(root["tasks"], "tasks");
    for (const [task, profileValue] of Object.entries(rawTasks)) {
      if (!isLlmTaskId(task)) throw configError("tasks contains an unknown task id.", [task]);
      const profile = stringAt(profileValue, `tasks.${task}`);
      if (!models[profile]) throw configError(`tasks.${task} references an unknown model profile.`, [profile]);
      tasks[task] = profile;
    }
  }
  return { version: 1, providers, models, tasks, source: "file", sourcePath };
}

export const BUILTIN_LLM_CONFIG: ResolvedLlmConfig = {
  version: 1,
  providers: {
    // baseUrl comes from PROJECT_LITELLM_GATEWAY_BASE_URL (see builtinLlmConfig);
    // null until resolved, so the built-in config never bakes in a host.
    gw: { protocol: "openai-compatible", baseUrl: null, apiKeyEnv: "PROJECT_LITELLM_GATEWAY_API_KEY", timeoutMs: 300_000, maxAttempts: 3 },
    // Video goes direct to Google (not the LiteLLM gateway); longer timeout for
    // upload+processing of large episode files. Credential: GEMINI_API_KEY.
    "google-gemini": { protocol: "google-gemini", baseUrl: null, apiKeyEnv: "GEMINI_API_KEY", timeoutMs: 600_000, maxAttempts: 3 },
  },
  models: {
    // DeepSeek v4 Flash is the default high-volume text profile. It supports the
    // OpenAI-compatible chat completions route used by the gateway.
    // temperature: 0 — ingest is all judgment/extraction (entity merges, state
    // classification, normalization), never creative writing. Deterministic
    // sampling stops run-to-run drift (a stray `## 合并` folding two distinct
    // characters into one) and keeps resume cache keys stable.
    default: { provider: "gw", model: "deepseek-v4-flash-packy", temperature: 0 },
    "ingest-low": { provider: "gw", model: "deepseek-v4-flash-packy", temperature: 0 },
    // Serial episode-assets → sonnet: native tool-calling with a name/aliases-split
    // schema separates canonical names from translations/past-life names far more
    // reliably than grok did (skeleton leads split correctly, no paren-polluted
    // names), which markdown/text mode could not hold on this model.
    "ingest-normalize": { provider: "gw", model: "claude-sonnet-4-6-aihubmix", temperature: 0 },
    "ingest-video": { provider: "google-gemini", model: "gemini-3.8-flash" },
    // `scriptctl review` names this profile directly rather than through a
    // task id: it drives its own agent loop instead of the router, and one
    // Gemini credential serves both the loop and its video checks.
    "review-agent": { provider: "google-gemini", model: "gemini-3.8-flash", temperature: 0 },
  },
  tasks: {
    // Ingest: cheap model for chunk normalization + pronoun resolve + recall;
    // everything else (governance/refine/state, synopsis, metadata) falls to
    // the high `default` model.
    "ingest.body-start": "ingest-low",
    "ingest.arc-summary": "ingest-low",
    "ingest.text-normalize": "ingest-low",
    "ingest.mention-resolve": "ingest-low",
    "ingest.asset-recall": "ingest-low",
    // Whole-script duplicate/state audit uses Sonnet native tool-calling for a
    // schema-bound set of findings.
    "ingest.asset-audit": "ingest-normalize",
    // Serial entity/look extraction and its unresolved-name repair share the same
    // schema-bound function-calling model.
    "ingest.episode-assets": "ingest-normalize",
    "ingest.name-fix": "ingest-normalize",
    // Video understanding → Gemini (§10.3).
    "ingest.video-serial": "ingest-video",
  },
  source: "builtin",
  sourcePath: null,
};

export function builtinLlmConfig(): ResolvedLlmConfig {
  const rawBaseUrl = (process.env.PROJECT_LITELLM_GATEWAY_BASE_URL ?? "").trim();
  const baseUrl = rawBaseUrl ? httpUrlAt(rawBaseUrl, "PROJECT_LITELLM_GATEWAY_BASE_URL") : null;
  return {
    ...BUILTIN_LLM_CONFIG,
    providers: {
      gw: { ...BUILTIN_LLM_CONFIG.providers["gw"]!, baseUrl },
      // Gemini is not gateway-routed; keep its SDK-default endpoint (null).
      "google-gemini": { ...BUILTIN_LLM_CONFIG.providers["google-gemini"]! },
    },
    models: { ...BUILTIN_LLM_CONFIG.models },
    tasks: { ...BUILTIN_LLM_CONFIG.tasks },
  };
}

function defaultConfigPath(): string {
  const xdg = (process.env.XDG_CONFIG_HOME ?? "").trim();
  return xdg ? path.join(xdg, "scriptctl", "llm.yaml") : path.join(os.homedir(), ".config", "scriptctl", "llm.yaml");
}

function readYamlConfig(filePath: string): ResolvedLlmConfig {
  if (!fs.existsSync(filePath)) throw configError("LLM configuration file does not exist.", [filePath]);
  const document = parseDocument(fs.readFileSync(filePath, "utf-8"), { uniqueKeys: true });
  if (document.errors.length > 0) throw configError("LLM configuration YAML is invalid.", document.errors.map((error) => error.message));
  const value: unknown = document.toJS();
  return parseLlmConfig(value, path.resolve(filePath));
}

export function loadLlmConfig(explicitPath?: string): ResolvedLlmConfig {
  const flagPath = (explicitPath ?? "").trim();
  if (flagPath) return readYamlConfig(flagPath);
  const envPath = (process.env.SCRIPTCTL_LLM_CONFIG ?? "").trim();
  if (envPath) return readYamlConfig(envPath);
  const conventionalPath = defaultConfigPath();
  return fs.existsSync(conventionalPath) ? readYamlConfig(conventionalPath) : builtinLlmConfig();
}
